import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  CalendarX,
  CheckCircle,
  ChatCircleText,
  type Icon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateTimeWithOptionsBs } from "@/lib/format";

export type TherapistActionItemId =
  | "past_sessions"
  | "unanswered_reviews";

export interface TherapistActionItem {
  id: TherapistActionItemId;
  priority: "P1" | "P2" | "P3";
  count: number;
  oldestAt?: string | null;
}

interface ActionConfig {
  to: string;
  search?: Record<string, string>;
  icon: Icon;
}

const ACTION_CONFIG: Record<TherapistActionItemId, ActionConfig> = {
  past_sessions: { to: "/dashboard/therapist/therapies", search: { status: "scheduled" }, icon: CalendarX },
  unanswered_reviews: { to: "/dashboard/therapist/reviews", icon: ChatCircleText },
};

type Tone = "rose" | "amber" | "blue";

const PRIORITY_TONE: Record<TherapistActionItem["priority"], Tone> = {
  P1: "rose",
  P2: "amber",
  P3: "blue",
};

const PILL_STYLE: Record<Tone, string> = {
  rose: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  amber: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  blue: "bg-primary/10 text-primary ring-1 ring-primary/15",
};

const COUNT_STYLE: Record<Tone, string> = {
  rose: "bg-rose-600 text-white",
  amber: "bg-amber-500 text-white",
  blue: "bg-primary text-primary-foreground",
};

const TAG_STYLE: Record<TherapistActionItem["priority"], string> = {
  P1: "bg-rose-600 text-white",
  P2: "bg-amber-500 text-white",
  P3: "bg-muted text-muted-foreground",
};

function shouldShowItem(item: TherapistActionItem): boolean {
  return item.count > 0;
}

function formatOldestAt(value: string | null | undefined): string | null {
  if (!value) return null;
  return formatDateTimeWithOptionsBs(value, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Sarajevo",
  });
}

export function TherapistActionInbox({ items }: { items: TherapistActionItem[] }) {
  const { t } = useTranslation();
  const visibleItems = items.filter(shouldShowItem);
  const totalCount = visibleItems.reduce((sum, item) => sum + item.count, 0);

  return (
    <section className="overflow-hidden rounded-2xl border border-border/50 bg-card shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-border/40 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            {t("therapist.dashboard.actionCenter")}
          </h2>
          <p className="text-sm text-muted-foreground">{t("therapist.dashboard.actionCenterSubtitle")}</p>
        </div>
        {totalCount > 0 && (
          <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-rose-600 px-2 py-1 text-xs font-semibold text-white tabular-nums">
            {totalCount}
          </span>
        )}
      </header>

      {visibleItems.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
          <CheckCircle className="size-7 text-emerald-600" weight="duotone" />
          <p className="text-sm font-medium text-foreground">{t("therapist.dashboard.actionCenterEmpty")}</p>
          <p className="text-sm text-muted-foreground">{t("therapist.dashboard.actionCenterEmptySub")}</p>
        </div>
      ) : (
        <div className="divide-y divide-border/40">
          {visibleItems.map((item) => {
            const config = ACTION_CONFIG[item.id];
            const ItemIcon = config.icon;
            const tone = PRIORITY_TONE[item.priority];
            const oldestAt = formatOldestAt(item.oldestAt);

            return (
              <div
                key={item.id}
                className={cn(
                  "relative grid items-center gap-4 px-5 py-4 transition-colors hover:bg-muted/40",
                  "grid-cols-[auto_1fr_auto]",
                )}
              >
                {item.priority === "P1" && (
                  <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-rose-600" />
                )}

                <span
                  className={cn(
                    "inline-flex size-11 shrink-0 items-center justify-center rounded-xl",
                    PILL_STYLE[tone],
                  )}
                >
                  <ItemIcon className="size-5" weight="duotone" />
                </span>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      {t(`therapist.dashboard.actions.${item.id}.title`)}
                    </p>
                    <span
                      className={cn(
                        "rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide",
                        TAG_STYLE[item.priority],
                      )}
                    >
                      {item.priority}
                    </span>
                    {item.count > 0 && (
                      <span
                        className={cn(
                          "inline-flex min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-bold tabular-nums",
                          COUNT_STYLE[tone],
                        )}
                      >
                        {item.count}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(`therapist.dashboard.actions.${item.id}.description`)}
                    {oldestAt ? (
                      <>
                        {" · "}
                        <span className={cn(item.priority === "P1" && "font-medium text-rose-600")}>
                          {t("therapist.dashboard.oldestAt", { date: oldestAt })}
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>

                <Button
                  asChild
                  size="sm"
                  variant={item.priority === "P1" ? "default" : "outline"}
                  className="w-auto"
                >
                  <Link to={config.to} search={config.search}>
                    {t(`therapist.dashboard.actions.${item.id}.cta`)}
                    <ArrowRight data-icon="inline-end" />
                  </Link>
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
